// 📄 services/disenoService.ts
// Diseñador de bocetos de iFlows (docs/plans/plan-visor-diagrama-iflows.md,
// E5). Los diseños son de la organización activa, como los iFlows del
// repositorio; todo (también la lectura) pasa por onehubApi
// (/v1/iflow-designs), que aplica las mismas reglas de acceso.
import { getAuth } from 'firebase/auth';
import { AristaDiagrama, DiagramaIflow, IflowApiError, NodoDiagrama, TipoNodoDiagrama } from './iflowService.ts';

export interface NodoDiseno {
  id: string;
  tipo: TipoNodoDiagrama | 'nota';
  subtipo: string;
  variante: string;
  nombre: string;
  x: number;
  y: number;
  ancho: number;
  alto: number;
  notas: string;
}

export interface AristaDiseno {
  id: string;
  tipo: 'secuencia' | 'mensaje';
  desde: string;
  hasta: string;
  nombre: string;
  condicion: string;
  notas: string;
  adaptador: { tipo: string; direccion: string } | null;
}

export interface ContenidoDiseno {
  nodos: NodoDiseno[];
  aristas: AristaDiseno[];
}

export interface ResumenDiseno {
  id: string;
  titulo: string;
  descripcion: string;
  autorUid: string;
  autorNombre: string;
  origen: { iflowId: string; version: number; titulo: string } | null;
  conteos: { pasos: number; conexiones: number };
  revision: number;
  creadoEn: string | null;
  actualizadoEn: string | null;
}

export interface Diseno extends ResumenDiseno {
  contenido: ContenidoDiseno;
  puedeEditar: boolean;
}

// Espejo de LIMITES_DISENO (functions/services/iflowDiseno.js).
export const MAX_NODOS_DISENO = 500;

const pedir = async <T,>(metodo: string, ruta: string, body?: object): Promise<T> => {
  const user = getAuth().currentUser;
  if (!user) throw new Error('No se detectó una sesión activa. Por favor, reingresa al sistema.');
  const res = await fetch(`${process.env.REACT_APP_API_URL}/v1/iflow-designs${ruta}`, {
    method: metodo,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${await user.getIdToken()}` },
    body: body === undefined ? undefined : JSON.stringify(body)
  });
  if (!res.ok) {
    const { code = 'ERROR', error, ...extra } = await res.json().catch(() => ({}));
    throw new IflowApiError(res.status, code, error || `Error ${res.status} al comunicarse con el servidor.`, extra);
  }
  return res.json();
};

export const listarDisenos = (orgId: string) => pedir<{ disenos: ResumenDiseno[] }>('GET', `/${orgId}`).then((r) => r.disenos);
export const obtenerDiseno = (orgId: string, id: string) => pedir<Diseno>('GET', `/${orgId}/${id}`);
export const crearDiseno = (orgId: string, titulo: string, descripcion = '', contenido?: ContenidoDiseno) =>
  pedir<{ id: string; revision: number }>('POST', `/${orgId}`, { title: titulo, description: descripcion, content: contenido });
export const guardarDiseno = (orgId: string, id: string, cambios: { titulo?: string; descripcion?: string; contenido?: ContenidoDiseno }, revisionBase: number) =>
  pedir<{ id: string; revision: number }>('PUT', `/${orgId}/${id}`, {
    title: cambios.titulo, description: cambios.descripcion, content: cambios.contenido, baseRevision: revisionBase
  });
export const borrarDiseno = (orgId: string, id: string) => pedir<{ id: string }>('DELETE', `/${orgId}/${id}`);
export const duplicarDiseno = (orgId: string, id: string) => pedir<{ id: string; revision: number }>('POST', `/${orgId}/${id}/duplicate`);
export const disenoDesdeIflow = (orgId: string, iflowId: string, version?: number) =>
  pedir<{ id: string; revision: number }>('POST', `/${orgId}/from-iflow`, { iflowId, version });

// ── Boceto -> formato del visor (vista previa y exportación) ──

/** Puntos de una flecha entre dos cajas: sale por el borde que mira al destino, con un codo. */
const puntosEntre = (a: NodoDiseno, b: NodoDiseno): [number, number][] => {
  const ca = [a.x + a.ancho / 2, a.y + a.alto / 2];
  const cb = [b.x + b.ancho / 2, b.y + b.alto / 2];
  if (Math.abs(cb[0] - ca[0]) >= Math.abs(cb[1] - ca[1])) {
    const sale: [number, number] = [cb[0] > ca[0] ? a.x + a.ancho : a.x, ca[1]];
    const llega: [number, number] = [cb[0] > ca[0] ? b.x : b.x + b.ancho, cb[1]];
    const medio = (sale[0] + llega[0]) / 2;
    return [sale, [medio, sale[1]], [medio, llega[1]], llega];
  }
  const sale: [number, number] = [ca[0], cb[1] > ca[1] ? a.y + a.alto : a.y];
  const llega: [number, number] = [cb[0], cb[1] > ca[1] ? b.y : b.y + b.alto];
  const medio = (sale[1] + llega[1]) / 2;
  return [sale, [sale[0], medio], [llega[0], medio], llega];
};

/** El visor dibuja un boceto igual que un iFlow (y lo exporta a PNG/SVG). */
export const disenoADiagrama = (c: ContenidoDiseno): DiagramaIflow => {
  const porId = new Map(c.nodos.map((n) => [n.id, n]));
  const nodos: NodoDiagrama[] = c.nodos.map((n) => ({
    id: n.id, tipo: n.tipo === 'nota' ? 'otro' : n.tipo, subtipo: n.subtipo, variante: n.variante,
    nombre: n.nombre, x: n.x, y: n.y, ancho: n.ancho, alto: n.alto, padre: '', detalle: null
  }));
  const aristas: AristaDiagrama[] = c.aristas
    .filter((a) => porId.has(a.desde) && porId.has(a.hasta))
    .map((a) => ({
      id: a.id, tipo: a.tipo, nombre: a.nombre || (a.adaptador?.tipo ?? ''), desde: a.desde, hasta: a.hasta,
      puntos: puntosEntre(porId.get(a.desde)!, porId.get(a.hasta)!),
      adaptador: a.adaptador ? { tipo: a.adaptador.tipo, direccion: a.adaptador.direccion, transporte: '', mensaje: '', autenticacion: '', conectividad: '', valores: [] } : null,
      condicion: a.condicion, porDefecto: false
    }));
  const xs = c.nodos.flatMap((n) => [n.x, n.x + n.ancho]);
  const ys = c.nodos.flatMap((n) => [n.y, n.y + n.alto]);
  const x = xs.length ? Math.min(...xs) : 0;
  const y = ys.length ? Math.min(...ys) : 0;
  return {
    version: 0,
    limites: { x, y, ancho: xs.length ? Math.max(...xs) - x : 400, alto: ys.length ? Math.max(...ys) - y : 300 },
    nodos,
    aristas,
    valoresVisibles: false
  };
};
